import { TextBadge } from '@carvana/ds-badge';
import LinkButton from '@carvana/ds-link-button';
import { NavStep } from '@carvana/ds-nav-step';
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { CheckItOutContext } from '|/context';
import useStepNameTransformer from '|/hooks/use-step-name-transformer';
import styles from './sections.module.css';

interface Step {
  id: string;
  name: string;
  order: number;
  section: number;
  sectionName: string;
  isEnabled: boolean;
  isComplete: boolean;
  url: string;
}

type RouteConfig = Record<
  string,
  {
    completionTimeEstimate?: number;
    [key: string]: unknown;
  }
>;

interface NavigationSectionProps {
  sectionName: string;
  sectionNumber: number;
  steps: Step[];
  latestStep: Step | undefined;
  pathname: string;
  stepUrlMap: Record<string, string>;
  route: string;
  statusMappings: Record<number, string | undefined>;
  routeNameComponentLookup: RouteConfig;
  currentSection?: number;
  handleEditClick: (sectionName?: string) => void;
  handleStepClick: (isDisabled: boolean, stepUrl: string) => boolean;
  allSteps?: Step[];
  allSections?: { sectionNumber: number; sectionName: string }[];
  viewedSection?: number;
  defaultStatus?: string;
}

const NavigationSection: React.FC<NavigationSectionProps> = ({
  sectionName,
  sectionNumber,
  steps,
  latestStep,
  pathname,
  stepUrlMap,
  statusMappings,
  routeNameComponentLookup,
  currentSection,
  handleEditClick,
  handleStepClick,
  allSteps = [],
  allSections = [],
  viewedSection,
  defaultStatus
}) => {
  const { transformStepName } = useStepNameTransformer();
  const { isCheckItOutTreatment } = useContext(CheckItOutContext);
  const activeStepOrder = latestStep?.order ?? 0;
  const areSectionStepsComplete = steps.every(step => step.isComplete);

  const isCurrentSection = (sectionNumber: number, currentSection?: number) =>
    currentSection === sectionNumber;

  const areAllStepsComplete = (steps: Step[]) =>
    steps.length > 0 && steps.every(step => step.isComplete);

  const getSectionOrderIndex = (
    sectionNumber: number,
    allSections: { sectionNumber: number }[]
  ) => {
    const sectionIds = allSections.map(s => s.sectionNumber).sort((a, b) => a - b);
    return sectionIds.indexOf(sectionNumber);
  };

  const calculateEmptySectionStatus = (sectionNumber: number, currentSection?: number) => {
    const currentSectionIndex = getSectionOrderIndex(currentSection ?? 0, allSections);
    const thisSectionIndex = getSectionOrderIndex(sectionNumber, allSections);

    if (thisSectionIndex > currentSectionIndex) {
      return 'Up next';
    }

    if (thisSectionIndex < currentSectionIndex) {
      return 'Complete';
    }

    return '';
  };

  const getSectionStatus = () => {
    const currentSection = latestStep?.section;

    const displayedStepsInSection = allSteps.filter(
      step => step.section === sectionNumber && step.isEnabled
    );

    if (isCurrentSection(sectionNumber, currentSection)) {
      return statusMappings[sectionNumber] ?? defaultStatus ?? 'In progress';
    }

    if (displayedStepsInSection.length > 0 && areAllStepsComplete(displayedStepsInSection)) {
      if (currentSection && currentSection > sectionNumber) {
        return 'Complete';
      }

      if (currentSection && currentSection <= sectionNumber) {
        return 'Up next';
      }
    }

    if (steps.length === 0 && displayedStepsInSection.length === 0) {
      return calculateEmptySectionStatus(sectionNumber, currentSection);
    }

    if (currentSection && currentSection < sectionNumber) {
      return 'Up next';
    }

    return defaultStatus ?? '';
  };

  const sectionStatus = getSectionStatus();
  const isViewedSection = sectionNumber === viewedSection;
  const hideStepsForTreatment =
    isCheckItOutTreatment && !isViewedSection && sectionName === 'Checkout';
  const shouldShowSteps =
    steps.length > 0 && (!areSectionStepsComplete || isViewedSection) && !hideStepsForTreatment;

  const getBadgePriority = (status: string): 'muted' | 'success' | 'informational' => {
    if (status === 'Complete') return 'success';
    if (status === 'Up next') return 'muted';
    return 'informational';
  };

  const shouldShowSection = () => {
    const currentSection = latestStep?.section;

    if (sectionNumber === 1) {
      return true;
    }

    return (
      currentSection === sectionNumber ||
      (currentSection && currentSection <= sectionNumber) ||
      areSectionStepsComplete
    );
  };

  if (!shouldShowSection()) {
    return null;
  }

  const shouldShowEdit = () => {
    if (sectionNumber !== 1 || steps.length === 0) {
      return false;
    }

    return (
      areSectionStepsComplete && currentSection !== undefined && currentSection > sectionNumber
    );
  };

  return (
    <section className={styles.sectionContainer}>
      {!(isCheckItOutTreatment && sectionName === 'Purchase Process') && (
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{transformStepName(sectionName)}</h2>
          <div className={styles.titleWithEdit}>
            {shouldShowEdit() && (
              <LinkButton
                onClick={() => {
                  handleEditClick(sectionName);
                }}
                size="sm"
                underline={false}
                data-analytics-id={`pp_navrail_edit_${sectionName.toLowerCase().replace(/\s+/g, '_')}`}
                data-testid={`navigation-rail-edit-${sectionName.toLowerCase().replace(/\s+/g, '_')}`}
              >
                Edit
              </LinkButton>
            )}
            {sectionStatus && (
              <TextBadge variant="subtle" priority={getBadgePriority(sectionStatus)}>
                {sectionStatus}
              </TextBadge>
            )}
          </div>
        </div>
      )}

      {shouldShowSteps && (
        <nav className={styles.stepsContainer}>
          {steps.map(step => {
            const stepUrl = stepUrlMap[step.id] ?? step.url;
            const isActive =
              pathname === `/steps/${stepUrl}` || pathname.startsWith(`/steps/${stepUrl}/`);
            const isCompleted = step.isComplete;
            const isDisabled = step.order > activeStepOrder;

            const completionTimeEstimate =
              routeNameComponentLookup[step.id]?.completionTimeEstimate ?? 0;

            let progressState: 'inactive' | 'active' | 'completed' = 'inactive';
            if (isCompleted) progressState = 'completed';
            else if (step.order === activeStepOrder) progressState = 'active';

            return (
              <Link
                key={step.id}
                to={isDisabled ? '#' : `/steps/${stepUrl}`}
                className={styles.stepLink}
                onClick={e => {
                  if (isDisabled) {
                    e.preventDefault();
                    return;
                  }
                  handleStepClick(isDisabled, stepUrl);
                }}
                data-analytics-id={`pp_navrail_step_${stepUrl}`}
              >
                <NavStep
                  headline={transformStepName(step.name)}
                  bodyText={
                    isCompleted
                      ? 'Complete'
                      : `About ${completionTimeEstimate} minute${completionTimeEstimate > 1 ? 's' : ''}`
                  }
                  progressState={progressState}
                  isActive={isActive}
                  disabled={isDisabled}
                  showChevron={true}
                  variant="bar"
                  width="100%"
                />
              </Link>
            );
          })}
        </nav>
      )}
    </section>
  );
};

export default NavigationSection;
